import { Controller } from "@hotwired/stimulus"

// Friendly labels for each filter field the form might submit.
// Keys match the segment between `search[` and `]` in input names.
const FILTER_MESSAGES = {
    lead_linkedin_url: "Excluding flagged LinkedIn profiles…",
    first_name: "Matching first names…",
    last_name: "Matching last names…",
    full_name: "Matching names…",
    job_title: "Matching job titles…",
    seniority: "Filtering by seniority…",
    functions: "Filtering by function…",
    departments: "Filtering by department…",
    skills: "Scanning skills on profiles…",
    lead_location_city: "Filtering by lead city…",
    lead_location_country: "Filtering by lead country…",
    lead_location_state: "Filtering by lead region…",
    lead_location_continent: "Filtering by lead continent…",
    company_name: "Matching company names…",
    company_domain: "Matching company domains…",
    company_industry: "Filtering by industry…",
    company_technologies: "Filtering by tech stack…",
    company_headquarter_country: "Filtering by company HQ country…",
    company_headquarter_city: "Filtering by company HQ city…",
    company_headquarter_state: "Filtering by company HQ region…",
    company_headcount: "Checking employee range…",
    company_keywords: "Matching company keywords…",
    company_linkedin: "Matching company LinkedIn pages…",
    excluded_list_ids: "Excluding leads from past exports…",
    uuid: "Excluding already-seen leads…",
    limit_per_company: "Capping leads per company…"
}

// Generic messages always shown alongside filter-derived ones.
const ALWAYS_ON = {
    start: "Querying data providers in parallel…",
    freshness: "Real-time verification & freshness checks…",
    accuracy: "Cross-checking sources for accuracy…",
    end: "Almost there — polishing your list…"
}

const FALLBACK_MESSAGES = [
    ALWAYS_ON.start,
    ALWAYS_ON.freshness,
    ALWAYS_ON.accuracy,
    ALWAYS_ON.end
]

export default class extends Controller {
    static targets = ["spinner", "content", "messageText"]

    connect() {
        this.minDelay = 5000 // 5 seconds minimum loader
        this.spinnerVisibleAt = null
        this.timeoutId = null
        this.rotationInterval = null
        this.messages = FALLBACK_MESSAGES
        this.messageIdx = 0

        // Bind event handlers so we can remove them later
        this.submitStartHandler = () => this.showLoader()
        this.submitEndHandler = () => this.hideLoader()

        this.element.addEventListener("turbo:submit-start", this.submitStartHandler)
        this.element.addEventListener("turbo:submit-end", this.submitEndHandler)
    }

    disconnect() {
        this.element.removeEventListener("turbo:submit-start", this.submitStartHandler)
        this.element.removeEventListener("turbo:submit-end", this.submitEndHandler)

        if (this.timeoutId) clearTimeout(this.timeoutId)
        if (this.rotationInterval) clearInterval(this.rotationInterval)
    }

    showLoader() {
        this.spinnerTarget.classList.remove("hidden")
        this.contentTarget.classList.add("hidden")
        this.spinnerVisibleAt = Date.now()

        this.messages = this.buildMessages()
        this.messageIdx = 0
        this.renderMessage()
        this.startRotation()
    }

    hideLoader() {
        const elapsed = Date.now() - this.spinnerVisibleAt
        const remaining = this.minDelay - elapsed

        const finish = () => {
            this.spinnerTarget.classList.add("hidden")
            this.contentTarget.classList.remove("hidden")
            this.stopRotation()
            this.timeoutId = null
        }

        if (remaining > 0) {
            this.timeoutId = setTimeout(finish, remaining)
        } else {
            finish()
        }
    }

    startRotation() {
        this.stopRotation()
        if (this.messages.length <= 1) return
        this.rotationInterval = setInterval(() => {
            this.messageIdx = (this.messageIdx + 1) % this.messages.length
            this.renderMessage()
        }, 2200)
    }

    stopRotation() {
        if (this.rotationInterval) {
            clearInterval(this.rotationInterval)
            this.rotationInterval = null
        }
    }

    renderMessage() {
        if (!this.hasMessageTextTarget || this.messages.length === 0) return
        this.messageTextTarget.textContent = this.messages[this.messageIdx]
    }

    // Derive an ordered list of messages based on which filters are populated
    // on the lead_finder form at submit time.
    buildMessages() {
        const form = document.getElementById('lead_finder_form') || this.element.querySelector('form')
        if (!form) return FALLBACK_MESSAGES

        const activeKeys = new Set()
        const inputs = form.querySelectorAll('input[name^="search["], select[name^="search["]')

        inputs.forEach(input => {
            const match = input.name.match(/^search\[([^\]]+)\]/)
            if (!match) return
            const key = match[1]

            // For checkboxes, only count when checked
            if (input.type === "checkbox" && !input.checked) return

            const raw = input.value
            if (raw === undefined || raw === null) return
            const value = String(raw).trim()
            if (value === "" || value === "0" || value === "false") return

            activeKeys.add(key)
        })

        const filterMessages = []
        activeKeys.forEach(key => {
            const msg = FILTER_MESSAGES[key]
            if (msg) filterMessages.push(msg)
        })

        if (filterMessages.length === 0) return FALLBACK_MESSAGES

        // Interleave for narrative pacing: open with provider query, drop in
        // filter checks, then real-time verification, more filters, accuracy,
        // and finally polishing.
        const sequence = [ALWAYS_ON.start]
        sequence.push(...filterMessages.slice(0, 2))
        sequence.push(ALWAYS_ON.freshness)
        if (filterMessages.length > 2) sequence.push(...filterMessages.slice(2, 5))
        sequence.push(ALWAYS_ON.accuracy)
        if (filterMessages.length > 5) sequence.push(...filterMessages.slice(5))
        sequence.push(ALWAYS_ON.end)

        return sequence
    }
}
